import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { QualificationTrainingsStatus } from '@wilson/interfaces';
import {
  FetchLabelsIfNeeded,
  GetLabelByIdPipe,
  LabelsState,
} from '@wilson/labels';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { Subject } from 'rxjs';
import { QualificationTrainingStatusTagComponent } from '../../qualification-training-status-tag/qualification-training-status-tag.component';
import { TrainingQualificationsBulkEditSelectionHasMixedValues } from '../../../../types';

@Component({
  selector: 'wilson-training-qualifications-bulk-edit-general-section',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    GetLabelByIdPipe,
    NzCollapseModule,
    NzFormModule,
    NzInputModule,
    NzSelectModule,
    NzTooltipModule,
    QualificationTrainingStatusTagComponent,
    ReactiveFormsModule,
    TranslatePipe,
  ],
  templateUrl:
    './training-qualifications-bulk-edit-general-section.component.html',
  styleUrl:
    './training-qualifications-bulk-edit-general-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrainingQualificationsBulkEditGeneralSectionComponent
  implements OnInit, OnDestroy
{
  private store = inject(Store);

  private destroyed$ = new Subject<void>();
  private _containsTrainingsFromDigitalProvider = false;
  private statusOptions = [
    QualificationTrainingsStatus.open,
    QualificationTrainingsStatus.pending,
    QualificationTrainingsStatus.booked,
    QualificationTrainingsStatus.inProgress,
    QualificationTrainingsStatus.paused,
    QualificationTrainingsStatus.completedSuccess,
    QualificationTrainingsStatus.completedFailed,
    QualificationTrainingsStatus.resultAccepted,
    QualificationTrainingsStatus.resultRejected,
  ];

  @Input() active: boolean | null = true;
  @Input() disabled: boolean | null = false;
  @Input() nameFormControl: FormControl<string | null> =
    new FormControl<string>('', [Validators.required]);
  @Input() labelFormControl: FormControl<string[] | null> = new FormControl<
    string[]
  >([]);
  @Input() statusFormControl: FormControl<QualificationTrainingsStatus | null> =
    new FormControl<QualificationTrainingsStatus | null>(null, [
      Validators.required,
    ]);
  @Input() hasMixedValues: Pick<
    TrainingQualificationsBulkEditSelectionHasMixedValues,
    'name' | 'label' | 'status'
  > = {
    name: false,
    label: false,
    status: false,
  };
  @Input() set containsTrainingsFromDigitalProvider(contains: boolean) {
    this._containsTrainingsFromDigitalProvider = contains;
    this.setCanStatusBeModified();
  }
  get containsTrainingsFromDigitalProvider(): boolean {
    return this._containsTrainingsFromDigitalProvider;
  }

  protected Validators = Validators;
  protected labels$ = this.store.select(LabelsState.labels);
  protected filteredStatusOptions = this.statusOptions;

  get currentStatusNotInFilteredOptions(): boolean {
    return (
      this.statusFormControl.value !== null &&
      !this.filteredStatusOptions.includes(this.statusFormControl.value)
    );
  }

  ngOnInit(): void {
    this.store.dispatch(new FetchLabelsIfNeeded());
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private setCanStatusBeModified(): void {
    if (this.containsTrainingsFromDigitalProvider) {
      this.filteredStatusOptions = [
        QualificationTrainingsStatus.resultAccepted,
        QualificationTrainingsStatus.resultRejected,
      ];
    } else {
      this.filteredStatusOptions = this.statusOptions;
    }
  }
}
